import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  CircularProgress,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Button
} from '@mui/material';
import { 
  Email,
  IntegrationInstructions,
  Block,
  Schedule,
  FilterList,
  Search,
  Clear
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function ReminderEmailsCard({ userId }) {
  const { showError } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [emailTypeFilter, setEmailTypeFilter] = useState('');

  useEffect(() => {
    fetchUserEmailsSent();
  }, [userId]);

  useEffect(() => {
    if (data?.emails_sent) {
      applyFilters();
    }
  }, [data, selectedProject, searchTerm, emailTypeFilter]);

  const applyFilters = () => {
    let filtered = data.emails_sent || [];

    // Filter by organization
    if (selectedProject) {
      filtered = filtered.filter(email => 
        email.organization_id === selectedProject
      );
    }

    // Filter by email type
    if (emailTypeFilter) {
      filtered = filtered.filter(email => 
        email.email_type === emailTypeFilter
      );
    }

    // Filter by search term (searches in email type, organization ID, and email ID)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(email => 
        email.email_type?.toLowerCase().includes(term) ||
        email.organization_id?.toLowerCase().includes(term) ||
        email.unique_id?.toLowerCase().includes(term)
      );
    }

    setFilteredEmails(filtered);
  };

  const fetchUserEmailsSent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchUserEmailsSent(userId);
      
      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error);
        showError(`Failed to load reminder emails: ${result.error}`, 'Error');
      }
    } catch (err) {
      setError('Failed to load reminder emails');
      showError('Failed to load reminder emails. Please try again.', 'Error');
      console.error('Failed to fetch user emails sent:', err);
    }
    
    setLoading(false);
  };

  const handleClearFilters = () => {
    setSelectedProject('');
    setSearchTerm('');
    setEmailTypeFilter('');
  };

  const getUniqueProjects = () => {
    if (!data?.emails_sent) return [];
    return [...new Set(data.emails_sent.map(email => email.organization_id).filter(Boolean))];
  };

  const getEmailTypeIcon = (emailType) => {
    switch (emailType) {
      case 'integration_reminder':
        return <IntegrationInstructions fontSize="small" />;
      case 'inactive_reminder':
        return <Block fontSize="small" />;
      default:
        return <Email fontSize="small" />;
    }
  };

  const getEmailTypeLabel = (emailType) => {
    switch (emailType) {
      case 'integration_reminder':
        return 'Integration Reminder';
      case 'inactive_reminder':
        return 'Inactive Reminder';
      default:
        return emailType;
    }
  };

  const getEmailTypeColor = (emailType) => {
    switch (emailType) {
      case 'integration_reminder':
        return 'primary';
      case 'inactive_reminder':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        py: 4
      }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          Loading reminder emails...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        py: 4
      }}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Failed to load reminder emails
          </Typography>
        </Box>
      </Box>
    );
  }

  const emails = filteredEmails;
  const totalCount = data?.total_count || 0;
  const filteredCount = filteredEmails.length;
  const uniqueProjects = getUniqueProjects();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start', 
          mb: 2,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Email sx={{ color: 'primary.main', fontSize: { xs: 24, sm: 28 } }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Reminder Emails
              </Typography>
             
            </Box>
          </Box>
          
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
          {/* Statistics in same row */}
          {data?.emails_sent?.length > 0 && (
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center', 
              gap: 3
            }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="primary.main" fontWeight={600}>
                  {totalCount}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                  Total
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="primary.main" fontWeight={600}>
                  {data.emails_sent.filter(e => e.email_type === 'integration_reminder').length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                  Integration
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="warning.main" fontWeight={600}>
                  {data.emails_sent.filter(e => e.email_type === 'inactive_reminder').length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                  Inactive
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="info.main" fontWeight={600}>
                  {uniqueProjects.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                  Projects
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Statistics for mobile/tablet */}
      {data?.emails_sent?.length > 0 && (
        <Box sx={{ 
          display: { xs: 'flex', md: 'none' }, 
          justifyContent: 'space-between', 
          gap: 1,
          mt: 2
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              {totalCount}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
              Total
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="primary.main" fontWeight={600}>
              {data.emails_sent.filter(e => e.email_type === 'integration_reminder').length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
              Integration
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              {data.emails_sent.filter(e => e.email_type === 'inactive_reminder').length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
              Inactive
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="info.main" fontWeight={600}>
              {uniqueProjects.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
              Projects
            </Typography>
          </Box>
        </Box>
      )}
      </Box>

      {/* Filters Section */}
      <Card sx={{ mb: 3, backgroundColor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            rowGap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <FilterList sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} />
              <Typography variant="body1" fontWeight={600} sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                Filters
              </Typography>
            </Box>
            
            <TextField
              size="small"
              placeholder="Search emails..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                minWidth: { xs: '100%', sm: 200, md: 180 },
                flex: { xs: '1 1 100%', sm: '0 1 auto' }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                    >
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', sm: 160, md: 140 },
              flex: { xs: '1 1 100%', sm: '0 1 auto' }
            }}>
              <InputLabel>Email Type</InputLabel>
              <Select
                value={emailTypeFilter}
                onChange={(e) => setEmailTypeFilter(e.target.value)}
                label="Email Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="integration_reminder">Integration Reminder</MenuItem>
                <MenuItem value="inactive_reminder">Inactive Reminder</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ 
              minWidth: { xs: '100%', sm: 160, md: 140 },
              flex: { xs: '1 1 100%', sm: '0 1 auto' }
            }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                label="Project"
              >
                <MenuItem value="">All Projects</MenuItem>
                {uniqueProjects.map((orgId) => (
                  <MenuItem key={orgId} value={orgId}>
                    {orgId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={!selectedProject && !searchTerm && !emailTypeFilter}
              sx={{ 
                flexShrink: 0,
                minWidth: { xs: '100%', sm: 'auto' },
                flex: { xs: '1 1 100%', sm: '0 0 auto' }
              }}
            >
              Clear
            </Button>
            
            <Chip 
              label={`${filteredCount} of ${totalCount} emails`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ 
                fontWeight: 600,
                flexShrink: 0,
                display: { xs: 'none', sm: 'flex' }
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Content */}
      {emails.length === 0 ? (
        <Card sx={{ 
          backgroundColor: 'grey.50', 
          border: '2px dashed', 
          borderColor: 'grey.300',
          textAlign: 'center',
          py: 4
        }}>
          <CardContent>
            <Email sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {data?.emails_sent?.length === 0 ? 'No Reminder Emails Sent' : 'No Emails Match Filters'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data?.emails_sent?.length === 0 
                ? 'This user hasn\'t received any reminder emails yet.'
                : 'Try adjusting your filters to see more results.'
              }
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer sx={{ 
            maxHeight: { xs: 400, sm: 500 },
            overflowX: 'auto'
          }}>
            <Table sx={{ minWidth: { xs: 600, sm: 800 } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}>
                    Sent Date
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}>
                    Project ID
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 700, 
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    py: { xs: 1, sm: 1.5 }
                  }}>
                    Email ID
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emails.map((email, index) => (
                  <TableRow 
                    key={email.unique_id || index}
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      '&:nth-of-type(even)': { backgroundColor: 'grey.25' },
                      '&:last-child td, &:last-child th': { border: 0 }
                    }}
                  >
                    <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                        {getEmailTypeIcon(email.email_type)}
                        <Chip
                          label={getEmailTypeLabel(email.email_type)}
                          color={getEmailTypeColor(email.email_type)}
                          size="small"
                          variant="filled"
                          sx={{ 
                            fontWeight: 600,
                            fontSize: { xs: '0.625rem', sm: '0.75rem' },
                            height: { xs: 20, sm: 24 }
                          }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule fontSize="small" sx={{ color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {formatDate(email.sent_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                      <Chip
                        label={email.organization_id || 'N/A'}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          maxWidth: { xs: 120, sm: 200 },
                          height: { xs: 20, sm: 24 },
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace',
                          fontSize: { xs: '0.625rem', sm: '0.75rem' },
                          backgroundColor: 'grey.100',
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.25, sm: 0.5 },
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          wordBreak: 'break-all',
                          maxWidth: { xs: 120, sm: 200 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {email.unique_id || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Table Footer with Results Count */}
          {emails.length > 0 && (
            <Box sx={{ 
              p: 2, 
              borderTop: '1px solid', 
              borderColor: 'divider',
              backgroundColor: 'grey.50',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredCount} of {totalCount} reminder emails
              </Typography>
              
              {/* Mobile-only chip */}
              <Chip 
                label={`${filteredCount} of ${totalCount} emails`}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ 
                  fontWeight: 600,
                  display: { xs: 'flex', sm: 'none' }
                }}
              />
            </Box>
          )}
        </Card>
      )}


    </Box>
  );
}

export default ReminderEmailsCard;
